import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { getToken } from '../lib/auth';
import { Icon } from '../lib/icons';
import { downloadOnlineExport } from '../lib/downloadExport';
import { downloadMemorialZip } from '../lib/exportZip';

interface MemorialItem {
  id: string;
  slug: string;
  deceasedName: string;
  deathDate: string | null;
  visibility: string;
  viewCount: number;
  tributeCount: number;
  myRole: string;
  createdAt: number;
}

export default function MyMemorials() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MemorialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/login');
      return;
    }
    load();
  }, [navigate]);

  async function load() {
    setLoading(true);
    const res = await api<{ items: MemorialItem[] }>('/memorials');
    if (res.ok && res.data) {
      setItems(res.data.items);
    } else {
      setError(errMsg(res, '加载失败'));
    }
    setLoading(false);
  }

  async function handleExport(m: MemorialItem) {
    try {
      await downloadOnlineExport({ memorialId: m.id, name: m.deceasedName });
    } catch (e) {
      alert(e instanceof Error ? e.message : '导出失败');
    }
  }

  async function handleZipExport(m: MemorialItem) {
    try {
      await downloadMemorialZip(m.id, m.deceasedName);
    } catch (e) {
      alert(e instanceof Error ? e.message : '导出失败');
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-100 to-ink-50">
      <header className="mx-auto flex max-w-4xl items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2 text-ink-800">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink-900 text-xs text-candle-300">
            墓
          </span>
          <span className="text-sm font-medium">线上墓迪</span>
        </Link>
        <Link
          to="/create"
          className="rounded-full bg-ink-900 px-5 py-2 text-xs text-ink-50 transition hover:bg-ink-800"
        >
          + 创建纪念馆
        </Link>
        <Link
          to="/settings"
          className="rounded-full border border-ink-300 px-5 py-2 text-xs text-ink-700 transition hover:bg-ink-100"
        >
          账号设置
        </Link>
      </header>

      <main className="mx-auto max-w-4xl px-6 pb-20">
        <h1 className="mb-8 font-serifcn text-2xl text-ink-900">我的纪念馆</h1>

        {loading && <p className="text-sm text-ink-500">加载中…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!loading && !error && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-ink-300 py-16 text-center">
            <p className="text-4xl"><Icon name="candle" /></p>
            <p className="mt-4 text-sm text-ink-500">还没有纪念馆</p>
            <Link
              to="/create"
              className="mt-5 inline-block rounded-full bg-ink-900 px-6 py-2.5 text-xs text-ink-50"
            >
              创建第一座
            </Link>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((m) => (
            <div
              key={m.id}
              className="group rounded-2xl border border-ink-200 bg-white/80 p-5 transition hover:border-ink-400 hover:shadow-sm"
            >
              <Link to={`/m/${m.slug}`} className="block">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-serifcn text-lg text-ink-900">{m.deceasedName}</h2>
                    <p className="mt-1 text-xs text-ink-500">
                      {m.deathDate ? `逝于 ${m.deathDate}` : '未记录忌日'}
                    </p>
                  </div>
                  <VisibilityTag v={m.visibility} />
                </div>

                <div className="mt-4 flex gap-4 text-xs text-ink-500">
                  <span>祭品 {m.tributeCount}</span>
                  <span>浏览 {m.viewCount}</span>
                  <span>{m.myRole === 'owner' ? '创建者' : '家属'}</span>
                </div>
              </Link>

              <div className="mt-4 flex items-center gap-4 border-t border-ink-100 pt-4">
                <button
                  onClick={() => handleExport(m)}
                  className="text-xs text-ink-600 underline decoration-ink-300 underline-offset-4 transition hover:text-ink-900"
                >
                  导出在线独立页
                </button>
                <button
                  onClick={() => handleZipExport(m)}
                  className="text-xs text-ink-600 underline decoration-ink-300 underline-offset-4 transition hover:text-ink-900"
                >
                  导出纪念册 ZIP
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 导出说明：在线独立页 + 纪念册，统一放在列表底部说明 */}
        <div className="mt-10 rounded-2xl border border-ink-200 bg-white/70 p-6">
          <p className="mb-4 font-serifcn text-base text-ink-900">关于导出</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-ink-800">导出在线独立页</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-500">
                由平台后端实时生成单个 HTML 文件，可独立打开、转发与网盘存档。
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-ink-800">导出纪念册（ZIP）</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-500">
                把整座纪念馆打包成一个可独立打开的网页文件。即使将来平台不在了，你手上这份依然完整可用，可传给子孙、存进网盘或刻录保存。
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function VisibilityTag({ v }: { v: string }) {
  const map: Record<string, { text: string; cls: string }> = {
    public: { text: '公开', cls: 'bg-emerald-50 text-emerald-700' },
    password: { text: '密码', cls: 'bg-amber-50 text-amber-700' },
    private: { text: '私密', cls: 'bg-ink-100 text-ink-600' },
  };
  const item = map[v] ?? map.public;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] ${item.cls}`}>{item.text}</span>
  );
}
